import {ADVERTISER} from '@/pages/routes'
import {CheckOutlined, CloseOutlined} from '@ant-design/icons'
import {Form, Input, InputNumber, Spin, Switch} from 'antd'
import {AdvertiserBean} from 'common/api/api'
import {advertiserApi} from 'common/api/entitiesApi'
import {AnchorsForm} from 'common/components/AnchorsForm'
import {ButtonsPanel} from 'common/components/ButtonsPanel'
import {TimeZoneSelector} from 'common/components/dictionary/TimeZoneSelector'
import {FormCard} from 'common/components/FormCard'
import {useAppContext} from 'common/context/AppContext'
import { queueListRepositionOnModifiedChange } from 'common/util/listNavigation.ts';
import {apiCall, getCopyName} from 'common/util/Util'
import {useEffect, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import { StatusSwitch } from 'common/components/StatusSwitch.tsx'

const AdvertiserForm = ({mode}) => {
    const [loading, setLoading] = useState(false)
    const [form] = Form.useForm();
    const [entity, setEntity] = useState<AdvertiserBean>({
        active: true
    } as AdvertiserBean)

    const navigateTo = useNavigate();
    const {id} = useParams<{ id?: string }>();
    const idNum = id ? parseInt(id) : 0
    const appContext = useAppContext()

    const deleteAdvertiser = () => {
        apiCall(setLoading, appContext,
            () => advertiserApi.deleteAdv(idNum),
            () => navigateTo(ADVERTISER)
        )
    };

    const handleSubmit = () => {
        form.validateFields().then(values => {
            const newEntity = {...entity, ...values};

            apiCall(setLoading, appContext,
                () => mode === 'edit' && id
                    ? advertiserApi.editAdv(newEntity, idNum)
                    : advertiserApi.createAdv(newEntity),
                (response) => {
                    queueListRepositionOnModifiedChange({
                        appContext,
                        routeKey: ADVERTISER,
                        previousEntity: entity,
                        responseEntity: response?.data,
                        enabled: mode === 'edit'
                    })

                    navigateTo(ADVERTISER);
                })
        })
    }

    useEffect(() => {
        if (mode === 'edit' || mode === 'copy') {
            apiCall(setLoading, appContext,
                () => advertiserApi.adv(idNum),
                (data) => {
                    if (data && data.data) {
                        const entityData = mode === 'copy' ? {...data.data, name: getCopyName(data.data.name)} : data.data
                        setEntity(entityData)
                        form.setFieldsValue(entityData)
                    }
                }
            )
        }
    }, [mode, id]);

    return (
        <Spin spinning={loading}>
            <AnchorsForm form={form} onFinish={handleSubmit}>
                <FormCard
                    title="Advertiser"
                    extra={
                        <StatusSwitch
                            entity={entity}
                            setEntity={setEntity}
                            tooltip="Controls whether the advertiser is active and allowed to run campaigns."
                        />
                    }
                >
                    <Form.Item name="name" rules={[{required: true, message: 'Please input Advertiser name'}]}
                               label="Name">
                        <Input/>
                    </Form.Item>

                    <Form.Item
                               name="revShare"
                               rules={[{required: true, message: 'RevShare is required'}]}
                               tooltip="The percentage of revenue shared with this advertiser."
                               label="RevShare">
                        <InputNumber step={1} min={0} max={99} style={{width: '100px'}} addonAfter="%"/>
                    </Form.Item>

                    <Form.Item
                               name="timezoneId"
                               rules={[{required: true, message: 'Please select Time Zone'}]}
                               tooltip="Defines the time zone used for reporting."
                               label="Time Zone"
                    >
                        <TimeZoneSelector/>
                    </Form.Item>

                    <Form.Item
                        name="trusted"
                        label="Trusted Advertiser"
                        tooltip="Mark the advertiser as trusted, allowing their traffic and creatives to bypass certain restrictions."
                        valuePropName="checked"
                        initialValue={false}
                    >
                        <Switch checkedChildren={<CheckOutlined/>} unCheckedChildren={<CloseOutlined/>}/>
                    </Form.Item>
                </FormCard>

                <ButtonsPanel onCancel={() => navigateTo(ADVERTISER)}
                              showDelete={mode === 'edit'} onDelete={() => deleteAdvertiser()}
                ></ButtonsPanel>
            </AnchorsForm>
        </Spin>
    )
}

export default AdvertiserForm
